import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import {
  X, User, Upload, AlertTriangle, Trophy, Zap, CreditCard, Trash2, ChevronRight, RotateCcw,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { restorePurchases } from '../lib/iap';
import { formatGrantExpiry } from '../lib/entitlement';
import {
  updateProfile, updateEmail, updatePassword,
  reauthenticateWithCredential, reauthenticateWithPopup, EmailAuthProvider, deleteUser,
} from 'firebase/auth';
import { doc, setDoc, deleteDoc, updateDoc, arrayRemove, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth, googleProvider } from '../firebase';
import { cn } from '../lib/utils';
import { TermsOfService, PrivacyPolicy } from './LegalModals';

interface ManageAccountModalProps {
  user: any;
  coachData?: any;
  teams?: any[];
  isElite?: boolean;
  /** Which source grants Elite. Decides where billing can actually be managed. */
  eliteSources?: { stripe: boolean; iap: boolean; grant: boolean };
  /** Season Pass end date (epoch ms), or null for a founding comp / no grant. */
  grantExpiresAt?: number | null;
  onClose: () => void;
  onManageBilling?: () => void;
  onUpgrade?: () => void;
}

export const ManageAccountModal = ({
  user, coachData, teams = [], isElite = false, eliteSources, grantExpiresAt = null, onClose, onManageBilling, onUpgrade,
}: ManageAccountModalProps) => {
  const isGoogleUser = auth.currentUser?.providerData?.some(p => p.providerId === 'google.com') ?? false;

  // ── Profile state (relocated from SettingsView) ──
  const [profileName, setProfileName] = useState(auth.currentUser?.displayName || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  // Prefer the Firestore users-doc photo (holds uploaded base64) over Auth's
  // photoURL, so a reopened modal shows the saved photo.
  const [profilePhotoURL, setProfilePhotoURL] = useState(coachData?.photoURL || auth.currentUser?.photoURL || '');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // ── Restore Purchases (native iOS / Apple IAP only) ──
  const isNative = Capacitor.isNativePlatform();
  const [restoring, setRestoring] = useState(false);
  const [restoreMsg, setRestoreMsg] = useState<string | null>(null);

  async function handleRestore() {
    setRestoring(true);
    setRestoreMsg(null);
    const { isElite: restored, error } = await restorePurchases();
    setRestoring(false);
    if (error) setRestoreMsg(error);
    else if (restored) setRestoreMsg('Elite restored. You’re all set.');
    else setRestoreMsg('No previous Elite purchase found to restore.');
  }
  const [profileCurrentPassword, setProfileCurrentPassword] = useState('');
  const [profileNewPassword, setProfileNewPassword] = useState('');
  const [profileConfirmPassword, setProfileConfirmPassword] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);

  // ── Delete-account state ──
  // ── Where this coach's billing actually lives ──────────────────────────────
  // Entitlement is the OR of three sources, but only one of them can be cancelled
  // from any given place:
  //   apple → iOS Settings, reachable only on device
  //   web   → the Stripe portal, reachable only on the web
  //   none  → a comped grant; there is no subscription to manage at all
  //
  // The bug this fixes: "Manage Subscription" always opened iOS Settings on
  // native, so a coach who subscribed on the web saw an EMPTY Apple screen and
  // concluded they could not cancel. Same dead end for a comped grant on web,
  // where the portal call fails for a customer with no subscription and the error
  // is swallowed, so the button silently does nothing. In both cases the honest
  // answer is a sentence, not a button that goes nowhere.
  const billingHome: 'apple' | 'web' | 'none' =
    eliteSources?.iap ? 'apple' : eliteSources?.stripe ? 'web' : 'none';
  // A button is only offered where the destination actually exists.
  const canManageBillingHere =
    billingHome === 'apple' ? isNative : billingHome === 'web' ? !isNative : false;

  // Only a time-limited Season Pass has an end date. A founding comp has no
  // expiry and must never be told its access ends.
  const seasonPassEnds = formatGrantExpiry(grantExpiresAt);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Active Stripe subscription = hard block on deletion (so nobody deletes their account
  // while still billed). NOTE: this is Stripe-only — manual "founding coach" Elite grants
  // have no Stripe subscription and must NOT be blocked.
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subCheckLoading, setSubCheckLoading] = useState(true);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) { setSubCheckLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(db, 'customers', uid, 'subscriptions'),
          where('status', 'in', ['active', 'trialing']),
        );
        const snap = await getDocs(q);
        if (!cancelled) setHasActiveSubscription(!snap.empty);
      } catch {
        // Permission denied / no Stripe customer yet → treat as no active subscription.
        if (!cancelled) setHasActiveSubscription(false);
      } finally {
        if (!cancelled) setSubCheckLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // ── Legal ──
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const headedTeams = teams.filter(t => t?.coachId === user?.uid);
  const assistedTeams = teams.filter(t => t?.coachId !== user?.uid && (t?.coaches ?? []).includes(user?.uid));

  // Resize a source data URL to a <=200px JPEG and persist it as the profile
  // photo. Rejects (rather than hanging) if the browser can't decode the image —
  // the old code had no img.onerror, so a bad decode left the spinner spinning
  // forever. Returns a promise so both the native and web paths can await + catch.
  const resizeAndSavePhoto = (sourceDataUrl: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const u = auth.currentUser;
      if (!u) { reject(new Error('Not signed in')); return; }
      const img = new Image();
      img.onload = async () => {
        try {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 200;
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
          } else {
            if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Could not process image')); return; }
          ctx.drawImage(img, 0, 0, width, height);
          const base64 = canvas.toDataURL('image/jpeg', 0.8);
          // Store the photo ONLY in the Firestore users doc — NOT in Firebase
          // Auth's photoURL. Auth caps photoURL near 2048 chars and rejects a
          // base64 data URL with auth/invalid-profile-attribute. The avatar
          // reads coachData.photoURL (Firestore) first, so this is the source
          // of truth for uploaded photos; Google sign-in still populates Auth's
          // photoURL with its short https URL, which Firestore also mirrors.
          await setDoc(doc(db, 'users', u.uid), { photoURL: base64, updatedAt: new Date().toISOString() }, { merge: true });
          setProfilePhotoURL(base64);
          resolve();
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error("That image couldn't be read"));
      img.src = sourceDataUrl;
    });

  // Native (iOS): use the Camera plugin's native picker. It shows the OS
  // "Take Photo / Photo Library" prompt via UIImagePickerController/PHPicker and
  // returns a decoded JPEG data URL — bypassing the WKWebView <input type=file>
  // camera bridge, which fails on this app (FigCaptureSourceRemote err=-17281)
  // and hands back an undecodable HEIC buffer (initImage err=-39).
  const handleNativePhoto = async () => {
    setPhotoError(null);
    setIsUploadingPhoto(true);
    try {
      const photo = await Camera.getPhoto({
        source: CameraSource.Prompt,        // "Take Photo" or "Photo Library"
        resultType: CameraResultType.DataUrl,
        quality: 90,
        width: 400,                          // plugin pre-scales natively (HEIC→JPEG)
        correctOrientation: true,
        promptLabelHeader: 'Profile Photo',
        promptLabelPhoto: 'Choose from Library',
        promptLabelPicture: 'Take Photo',
      });
      if (photo.dataUrl) {
        await resizeAndSavePhoto(photo.dataUrl);
      }
    } catch (error: any) {
      // The plugin throws "User cancelled photos app" on cancel — not an error.
      const msg = String(error?.message ?? error ?? '');
      if (!/cancel/i.test(msg)) {
        console.error('Failed to set profile photo', error);
        setPhotoError("Couldn't add that photo. Please try again.");
      }
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Web: keep the standard file input — it works in real browsers.
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setPhotoError(null);
    setIsUploadingPhoto(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target?.result as string);
        reader.onerror = () => reject(new Error("That file couldn't be read"));
        reader.readAsDataURL(file);
      });
      await resizeAndSavePhoto(dataUrl);
    } catch (error) {
      console.error('Failed to upload profile photo', error);
      setPhotoError("Couldn't upload that photo. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = ''; // allow re-selecting the same file after a failure
    }
  };

  // Save Profile only persists display name, email, and password (the photo
  // saves itself at pick-time). Disable the button until one of those actually
  // changed, so it can't report "nothing to save."
  const hasProfileChanges =
    profileName.trim() !== (auth.currentUser?.displayName || '') ||
    profileEmail.trim() !== (auth.currentUser?.email || '') ||
    profileNewPassword.length > 0;

  const handleSaveProfile = async () => {
    setProfileError(null);
    setProfileSuccess(null);
    const u = auth.currentUser;
    if (!u) return;

    const nameChanged = profileName.trim() !== (u.displayName || '');
    const emailChanged = profileEmail.trim() !== u.email;
    const passwordChanging = profileNewPassword.length > 0;
    // The photo is persisted the moment it's picked (resizeAndSavePhoto writes
    // to Firestore), so it isn't saved here — but if it's the only thing the
    // coach changed, don't tell them there's "nothing to save."
    const photoChanged = profilePhotoURL !== (coachData?.photoURL || auth.currentUser?.photoURL || '');

    if (!nameChanged && !emailChanged && !passwordChanging) {
      setProfileSuccess(photoChanged ? 'Profile photo saved.' : 'No changes to save.');
      return;
    }
    if (passwordChanging && profileNewPassword !== profileConfirmPassword) {
      setProfileError('New passwords do not match.');
      return;
    }
    if (passwordChanging && profileNewPassword.length < 6) {
      setProfileError('New password must be at least 6 characters.');
      return;
    }
    if ((emailChanged || passwordChanging) && !profileCurrentPassword) {
      setProfileError('Enter your current password to change email or password.');
      return;
    }

    setProfileSaving(true);
    try {
      if ((emailChanged || passwordChanging) && profileCurrentPassword) {
        const credential = EmailAuthProvider.credential(u.email!, profileCurrentPassword);
        await reauthenticateWithCredential(u, credential);
      }
      if (nameChanged) await updateProfile(u, { displayName: profileName.trim() });
      if (emailChanged) await updateEmail(u, profileEmail.trim());
      if (passwordChanging) await updatePassword(u, profileNewPassword);

      setProfileCurrentPassword('');
      setProfileNewPassword('');
      setProfileConfirmPassword('');
      setProfileSuccess('Profile updated successfully.');
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setProfileError('Current password is incorrect.');
      } else if (code === 'auth/email-already-in-use') {
        setProfileError('That email is already in use by another account.');
      } else if (code === 'auth/requires-recent-login') {
        setProfileError('Session expired. Enter your current password to re-authenticate.');
      } else {
        setProfileError(err?.message || 'Failed to update profile. Try again.');
      }
    } finally {
      setProfileSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    const u = auth.currentUser;
    if (!u) return;
    if (headedTeams.length > 0 || hasActiveSubscription) return; // guarded in UI; defensive here too

    setIsDeleting(true);
    try {
      // Re-authenticate (required by Firebase before deletion)
      if (isGoogleUser) {
        await reauthenticateWithPopup(u, googleProvider);
      } else {
        if (!deletePassword) {
          setDeleteError('Enter your password to confirm.');
          setIsDeleting(false);
          return;
        }
        await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email!, deletePassword));
      }

      // Step away from any teams the coach only assists (don't leave a dangling UID).
      for (const t of assistedTeams) {
        await updateDoc(doc(db, 'teams', t.id), { coaches: arrayRemove(u.uid) });
      }
      // Remove the user profile doc, then the auth account. Auth listener signs them out.
      await deleteDoc(doc(db, 'users', u.uid));
      await deleteUser(u);
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setDeleteError('Password is incorrect.');
      } else if (code === 'auth/requires-recent-login') {
        setDeleteError('Session expired. Sign out and back in, then try again.');
      } else if (code === 'auth/popup-closed-by-user') {
        setDeleteError('Re-authentication was cancelled.');
      } else {
        setDeleteError(err?.message || 'Failed to delete account. Try again.');
      }
      setIsDeleting(false);
    }
  };

  const inputCls = 'w-full bg-surface border border-hairline rounded-xl px-4 py-2 text-ink font-black outline-none focus:ring-2 focus:ring-emerald-500 transition-all placeholder:text-ink-2';
  const labelCls = 'text-[10px] font-bold text-ink-2 uppercase tracking-widest';

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-surface backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        className="bg-canvas w-full max-w-md rounded-3xl border border-hairline shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 shrink-0 border-b border-hairline">
          <h2 className="font-black uppercase text-ink text-base tracking-wide">Manage Account</h2>
          <button onClick={onClose} className="text-ink-2 hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (scrolls) */}
        <div className="px-6 py-5 overflow-y-auto space-y-8">

          {/* ── Profile ── */}
          <section className="space-y-5">
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-ink-2">Profile</h3>

            {isGoogleUser && (
              <div className="flex items-start space-x-3 p-3 bg-raised rounded-xl border border-ink-mute">
                <AlertTriangle className="w-3.5 h-3.5 text-gold-ink mt-0.5 shrink-0" />
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest leading-relaxed">
                  You signed in with Google. Email and password changes are managed through your Google account.
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-surface rounded-2xl border border-hairline flex items-center justify-center overflow-hidden shrink-0">
                  {profilePhotoURL ? (
                    <img src={profilePhotoURL} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-ink-2" />
                  )}
                </div>
                {isNative ? (
                  <button
                    type="button"
                    onClick={handleNativePhoto}
                    disabled={isUploadingPhoto}
                    className="flex-1 disabled:opacity-60"
                  >
                    <div className="w-full py-4 bg-surface border border-hairline rounded-2xl flex flex-col items-center justify-center space-y-2 hover:border-ink-mute transition-colors">
                      <Upload className="w-5 h-5 text-ink-2" />
                      <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                        {isUploadingPhoto ? 'Uploading...' : 'Add Photo'}
                      </span>
                    </div>
                  </button>
                ) : (
                  <label className="flex-1 cursor-pointer">
                    <div className="w-full py-4 bg-surface border border-hairline rounded-2xl flex flex-col items-center justify-center space-y-2 hover:border-ink-mute transition-colors">
                      <Upload className="w-5 h-5 text-ink-2" />
                      <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">
                        {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </span>
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={isUploadingPhoto} />
                  </label>
                )}
              </div>
              {photoError && (
                <p className="mt-2 text-[11px] font-bold text-red-500">{photoError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className={labelCls}>Display Name</label>
              <input type="text" value={profileName} onChange={e => setProfileName(e.target.value)} placeholder="Coach name" className={inputCls} />
            </div>

            {!isGoogleUser && (
              <>
                <div className="space-y-1.5">
                  <label className={labelCls}>Email</label>
                  <input type="email" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} placeholder="coach@example.com" className={inputCls} />
                </div>

                <div className="space-y-4">
                  <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Change Password</p>
                  <div className="space-y-1.5">
                    <label className={labelCls}>New Password</label>
                    <input type="password" value={profileNewPassword} onChange={e => setProfileNewPassword(e.target.value)} placeholder="Leave blank to keep current" className={inputCls} />
                  </div>
                  {profileNewPassword.length > 0 && (
                    <div className="space-y-1.5">
                      <label className={labelCls}>Confirm New Password</label>
                      <input type="password" value={profileConfirmPassword} onChange={e => setProfileConfirmPassword(e.target.value)} placeholder="Re-enter new password" className={inputCls} />
                    </div>
                  )}
                </div>

                {(profileEmail !== user?.email || profileNewPassword.length > 0) && (
                  <div className="space-y-1.5">
                    <label className={labelCls}>Current Password <span className="text-danger">*</span></label>
                    <input type="password" value={profileCurrentPassword} onChange={e => setProfileCurrentPassword(e.target.value)} placeholder="Required to change email or password" className={inputCls} />
                  </div>
                )}
              </>
            )}

            {profileError && <p className="text-[10px] font-semibold text-danger uppercase tracking-widest">{profileError}</p>}
            {profileSuccess && <p className="text-[10px] font-semibold text-accent-ink uppercase tracking-widest">{profileSuccess}</p>}

            <button
              onClick={handleSaveProfile}
              disabled={profileSaving || !hasProfileChanges}
              className="w-full py-3 bg-ink text-surface font-black text-xs uppercase tracking-widest rounded-xl hover:bg-ink transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {profileSaving && <div className="w-3.5 h-3.5 border-2 border-surface/30 border-t-black rounded-full animate-spin" />}
              <span>{profileSaving ? 'Saving...' : 'Save Profile'}</span>
            </button>
          </section>

          {/* ── Billing ── */}
          <section className="space-y-3">
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-ink-2">Membership</h3>
            <div className="p-4 bg-surface border border-hairline rounded-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black uppercase text-ink">Current Plan</span>
                  {isElite ? (
                    <span className="inline-flex items-center gap-0.5 text-[8px] font-black text-gold-ink bg-amber-400/15 px-1.5 py-0.5 rounded-full uppercase tracking-widest">
                      <Zap className="w-2 h-2" /> Elite
                    </span>
                  ) : (
                    <span className="text-[8px] font-black text-ink-2 bg-raised px-1.5 py-0.5 rounded-full uppercase tracking-widest">Free</span>
                  )}
                </div>
              </div>
              {/* Season Pass: name it and date it. A founding comp has no expiry,
                  so seasonPassEnds is null and this renders nothing — a comped
                  coach must never be told their access ends. */}
              {isElite && seasonPassEnds && (
                <p className="text-[11px] font-bold text-ink-2 mt-2 leading-relaxed">
                  Season Pass: free through <span className="text-ink">{seasonPassEnds}</span>.
                  After that, Elite features need a subscription. Your team, roster and
                  history stay yours either way.
                </p>
              )}
            </div>

            {isElite && canManageBillingHere ? (
              <button
                onClick={() => { onClose(); onManageBilling?.(); }}
                className="w-full flex items-center justify-between p-4 bg-surface border border-hairline hover:border-hairline rounded-2xl transition-colors group text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-gold-soft border border-hairline flex items-center justify-center">
                    <CreditCard className="w-4 h-4 text-gold-ink" />
                  </div>
                  <span className="text-xs font-black uppercase text-ink">Manage Subscription</span>
                </div>
                <ChevronRight className="w-4 h-4 text-ink-2" />
              </button>
            ) : isElite ? (
              // Deliberately plain text, never a tappable CTA. Telling a coach
              // where their own subscription lives is account information; a
              // button or link out to the web store would be steering, which
              // App Store guideline 3.1.1 does not allow from inside the app.
              <div className="p-4 bg-surface border border-hairline rounded-2xl flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gold-soft border border-hairline flex items-center justify-center shrink-0">
                  <CreditCard className="w-4 h-4 text-gold-ink" />
                </div>
                <p className="text-[11px] font-semibold text-ink-2 leading-relaxed pt-1">
                  {billingHome === 'web'
                    ? 'Your subscription was started on the web, so it is managed at dugoutbaseball.app. It will not appear in your iPhone subscription settings.'
                    : 'Your Elite access was granted by Dugout, so there is no subscription to manage and nothing will be charged.'}
                </p>
              </div>
            ) : (
              <button
                onClick={() => { onClose(); onUpgrade?.(); }}
                className="w-full flex items-center justify-between p-4 bg-surface border border-hairline hover:border-ink-mute rounded-2xl transition-colors group text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-canvas border border-hairline flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-accent-ink" />
                  </div>
                  <span className="text-xs font-black uppercase text-accent-ink">Upgrade to Elite</span>
                </div>
                <ChevronRight className="w-4 h-4 text-ink-2" />
              </button>
            )}

            {/* Restore Purchases — Apple requires this for subscription apps.
                Native-only: lets a coach re-grant Elite on a new device / reinstall. */}
            {isNative && (
              <>
                <button
                  onClick={handleRestore}
                  disabled={restoring}
                  className="w-full flex items-center justify-between p-4 bg-surface border border-hairline hover:border-ink-mute rounded-2xl transition-colors group text-left disabled:opacity-50"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-canvas border border-hairline flex items-center justify-center">
                      <RotateCcw className="w-4 h-4 text-ink-2" />
                    </div>
                    <span className="text-xs font-black uppercase text-ink">
                      {restoring ? 'Restoring…' : 'Restore Purchases'}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-ink-2" />
                </button>
                {restoreMsg && (
                  <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest px-1">{restoreMsg}</p>
                )}
              </>
            )}
          </section>

          {/* ── Delete Account ── */}
          <section className="space-y-3">
            <div className="p-4 bg-[#FBF0F0] border border-hairline rounded-2xl space-y-3">
              <div>
                <p className="text-xs font-black uppercase text-ink">Delete Account</p>
                <p className="text-[10px] text-ink-2 leading-relaxed mt-1">
                  Permanently removes your login and profile.
                </p>
                <p className="text-[10px] font-semibold text-danger uppercase tracking-widest mt-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 shrink-0" />
                  <span>This action cannot be undone.</span>
                </p>
              </div>

              {/* Guard: must clear owned teams first */}
              {headedTeams.length > 0 ? (
                <div className="p-3 bg-surface border border-hairline rounded-xl">
                  <p className="text-[10px] font-semibold text-gold-ink uppercase tracking-widest leading-relaxed">
                    You're the head coach of {headedTeams.length} team{headedTeams.length > 1 ? 's' : ''}. Delete {headedTeams.length > 1 ? 'them' : 'it'} first (Settings → Advanced) before deleting your account:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {headedTeams.map(t => (
                      <li key={t.id} className="text-[10px] font-black uppercase text-ink-2">• {t.name}</li>
                    ))}
                  </ul>
                </div>
              ) : subCheckLoading ? (
                <div className="p-3 flex items-center justify-center gap-2">
                  <div className="w-3.5 h-3.5 border-2 border-ink-mute border-t-zinc-400 rounded-full animate-spin" />
                  <span className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Checking subscription…</span>
                </div>
              ) : hasActiveSubscription ? (
                <div className="p-3 bg-surface border border-hairline rounded-xl space-y-3">
                  <p className="text-[10px] font-semibold text-gold-ink uppercase tracking-widest leading-relaxed">
                    You have an active subscription. Cancel it first so you're not billed again, then you can delete your account.
                  </p>
                  {/* The worst version of the dead end: a coach is told to cancel,
                      taps the button, and lands on an empty Apple screen with no
                      way forward. Where the cancel destination is unreachable from
                      here, say where it is instead of offering a button. */}
                  {canManageBillingHere ? (
                    <button
                      onClick={() => { onClose(); onManageBilling?.(); }}
                      className="w-full py-2.5 bg-gold-soft border border-hairline text-gold-ink hover:bg-gold-soft rounded-xl font-black text-[11px] uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                    >
                      <CreditCard className="w-3.5 h-3.5" /> Manage Subscription
                    </button>
                  ) : (
                    <p className="text-[10px] font-semibold text-ink-2 leading-relaxed">
                      {billingHome === 'web'
                        ? 'This subscription was started on the web, so cancel it at dugoutbaseball.app. It will not appear in your iPhone subscription settings.'
                        : 'This access was granted by Dugout rather than purchased, so there is nothing to cancel.'}
                    </p>
                  )}
                </div>
              ) : !deleteOpen ? (
                <button
                  onClick={() => { setDeleteOpen(true); setDeleteError(null); }}
                  className="w-full py-2.5 border border-red-500/40 text-danger hover:bg-[#FBF0F0] rounded-xl font-black text-[11px] uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete Account
                </button>
              ) : (
                <div className="space-y-3">
                  {!isGoogleUser && (
                    <div className="space-y-1.5">
                      <label className={labelCls}>Confirm your password</label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={e => setDeletePassword(e.target.value)}
                        placeholder="Enter your password"
                        className={inputCls}
                      />
                    </div>
                  )}
                  {isGoogleUser && (
                    <p className="text-[10px] text-ink-2 leading-relaxed">
                      You'll be asked to confirm with Google before your account is deleted.
                    </p>
                  )}
                  {deleteError && <p className="text-[10px] font-semibold text-danger uppercase tracking-widest">{deleteError}</p>}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setDeleteOpen(false); setDeletePassword(''); setDeleteError(null); }}
                      disabled={isDeleting}
                      className="flex-1 py-2.5 border border-ink-mute text-ink-2 hover:text-ink rounded-xl font-semibold text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      className="flex-1 py-2.5 bg-red-500 hover:bg-red-400 text-ink rounded-xl font-black text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isDeleting && <div className="w-3.5 h-3.5 border-2 border-ink/30 border-t-white rounded-full animate-spin" />}
                      {isDeleting ? 'Deleting...' : 'Permanently Delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Footer — legal links */}
        <div className="px-6 py-3 border-t border-hairline bg-surface text-center shrink-0">
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => setShowPrivacy(true)} className="text-[10px] font-bold text-ink-2 hover:text-ink-2 transition-colors">Privacy Policy</button>
            <span className="text-ink-2 text-[10px]">·</span>
            <button onClick={() => setShowTerms(true)} className="text-[10px] font-bold text-ink-2 hover:text-ink-2 transition-colors">Terms of Service</button>
          </div>
        </div>
      </motion.div>

      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </div>,
    document.body
  );
};
